import { publicAssetUrl } from '@chegg-wt/core/utils/url'
import styled, { css } from 'styled-components'
import { adjust, themeBreakPoints } from '@chegg-wt/core/theme/theme'
import { AspiraRegular } from '@chegg-wt/core/theme/sunkist/fonts'
import { SharedNav } from '@chegg-wt/core/theme/sunkist/nav'
import { ButtonReset } from '@chegg-wt/core/theme/sunkist/resets'
import { spacing, breakpoints } from '@chegg-ui/theme'
import { Popover } from '@chegg-ui/popover'

const HEADER_HEIGHT = 52

export const Header = styled.header`
  ${AspiraRegular};
  ${SharedNav};
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

export const LogoContainer = styled.div<{ isWritingAssistEnabledAndHomePage?: boolean }>`
  display: flex;
  ${({ isWritingAssistEnabledAndHomePage }) =>
    isWritingAssistEnabledAndHomePage &&
    `
  @media only screen and (min-width: ${breakpoints.lg.min}) {
    margin-left: ${spacing.px12};
  }
  `}
`

export const LogoLink = styled.a``

export const Logo = styled.img`
  display: inline-block;
  position: relative;
  width: auto;
  height: 30px;
`

export const Nav = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  height: 100%;

  &:focus-within > ul {
    display: block;
  }
`

export const AccountButtonWrapper = styled.div`
  margin: 12px 0;
`

export const RightNav = styled(Nav)`
  justify-content: flex-end;
  flex-grow: 1;
`

export const HeaderNav = styled(Nav)`
  justify-content: flex-start;
  flex-grow: 1;
  text-decoration: none;

  > a {
    text-decoration: none;
    position: relative;

    /* remove some default styling */
    :hover,
    :focus {
      text-decoration: none;
      color: inherit;
    }
  }
`

export const MenuButton = styled.button`
  ${ButtonReset};
  display: flex;
  margin: 0 16px;
  padding: 10px 0;
  &:hover {
    cursor: pointer;
  }
  &:hover + ul {
    display: block;
  }
`

export const DropdownMenu = styled.ul`
  top: ${HEADER_HEIGHT}px;
  background: #fff;
  padding: 0;
  border-radius: 2px;
  box-shadow: 0 1px 4px rgba(34, 25, 25, 0.1);
  border: 1px solid #ddd;
  z-index: 1000;
  & *:first-child {
    border-top: none;
  }

  &:before {
    content: '';
    position: absolute;
    border-style: solid;
    border-width: 0 13px 11px;
    border-color: #ddd transparent;
    width: 0;
    z-index: 0;
    top: -12px;
    right: 20px;
  }
  &:after {
    content: '';
    position: absolute;
    border-style: solid;
    border-width: 0 13px 11px;
    border-color: #fff transparent;
    width: 0;
    z-index: 1;
    top: -11px;
    right: 20px;
  }
`

export const DropdownItem = styled.li<{ hideOnSmall: boolean }>`
  padding: 0;
  border-top: 1px solid #f4f4f4;
  position: relative;
  list-style: none;

  &:hover {
    cursor: pointer;
    background: #fafafa;
  }

  ${({ hideOnSmall }) =>
    hideOnSmall &&
    css`
      @media (min-width: ${({ theme }) => themeBreakPoints.small(theme)}) {
        display: none;
      }
    `}
`

export const DropdownLink = styled.a`
  display: inline-block;
  padding: 6px 0 0 35px;
  color: ${({ theme }) => theme.Colors.forFonts.fColor19};
  vertical-align: sub;
  padding: 0;
  font-size: 13px;
  margin: 0 0 0 5px;
  text-decoration: none;

  background-size: 16px;
  background-position: 3px 21px;
  background-repeat: no-repeat;
  height: 58px;
  min-width: 195px;
  padding-left: 27px;
  line-height: 450%;

  &.citation-guides {
    background-image: ${`url('${publicAssetUrl('citation-guides.svg')}');`};
  }
  &.plus {
    background-image: ${`url('${publicAssetUrl('plus.svg')}');`};
  }
  &.about-us {
    background-image: ${`url('${publicAssetUrl('about-us.svg')}');`};
  }
  &.support {
    background-image: ${`url('${publicAssetUrl('support.svg')}');`};
  }
  &.projects {
    background-image: ${`url('${publicAssetUrl('projects.svg')}');`};
  }
  &.add {
    background-image: ${`url('${publicAssetUrl('add.svg')}');`};
  }
  &.settings {
    background-image: ${`url('${publicAssetUrl('settings.svg')}');`};
  }
  &.logout {
    background-image: ${`url('${publicAssetUrl('logout.svg')}');`};
  }
`

export const DropdownMenuLeft = styled(DropdownMenu)`
  left: 4px;
  &:before {
    left: 4px;
  }
  &:after {
    left: 4px;
  }
`

export const DropdownMenuRight = styled(DropdownMenu)`
  right: 4px;
  &:before {
    right: 3px;
  }
  &:after {
    right: 3px;
  }
`

export const PillBadge = styled.div<{ size?: string }>`
  color: ${(props) => props.theme.colors.white};
  background-color: ${(props) => props.theme.colors.green};
  font-size: 10px;
  border-radius: 10px;
  text-transform: uppercase;
  padding: ${(props) => (props.size === 'small' ? '0px 2px' : '2px 7px')};
  font-weight: 600;
  margin: 5px;
  border: 1px solid ${(props) => props.theme.colors.green};
`

export const PlusBadge = styled(PillBadge)`
  color: ${(props) => props.theme.colors.white};
  background-color: ${(props) => props.theme.colors.blue};
  border: 1px solid ${(props) => props.theme.colors.blue};

  @media only screen and (max-width: 350px) {
    display: none;
  }
`

const navLinks = css`
  min-width: fit-content;
  color: ${({ theme }) => theme.Colors.forFonts.fColor19};
  font-size: 14px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
`

export const NavLink = styled.a<{
  hideOnMobile: boolean
  isWritingAssistEnabledAndHomePage?: boolean
}>`
  ${navLinks};
  margin-right: 24px;

  ${({ hideOnMobile, isWritingAssistEnabledAndHomePage }) =>
    hideOnMobile &&
    css`
      @media only screen and (max-width: ${isWritingAssistEnabledAndHomePage
          ? breakpoints.md.max
          : ({ theme }) => adjust(themeBreakPoints.small(theme), -40)}) {
        display: none;
      }
    `}
`

export const Email = styled.span`
  ${navLinks};
  margin-left: 3px;

  @media only screen and (max-width: ${({ theme }) =>
      adjust(themeBreakPoints.extraSmall(theme), -50)}) {
    display: none;
  }
`

export const UserDetails = styled.button`
  ${ButtonReset}
  height: 100%;
  display: flex;
  align-items: center;
  text-align: right;
  color: ${({ theme }) => theme.Colors.forFonts.fColor19};
  cursor: pointer;
  margin-right: 10px;

  &:hover + ${DropdownMenuRight} {
    display: block;
  }

  &:hover {
    color: ${({ theme }) => theme.Colors.forFonts.fColor19};
  }
`

const navAfterBar = css`
  position: absolute;
  content: '';
  bottom: -4px;
  height: 0px;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.Colors.primaryColor};
  transition: bottom 0.2s;
`

export const HeaderNavLink = styled.a<{
  hideOnMobile: boolean
  isActive: boolean
  isWritingAssistantHomeEnabled: boolean
}>`
  color: ${({ theme }) => theme.Colors.forFonts.fColor19};
  font-size: ${({ theme }) => theme.font.text.size.lg};
  line-height: ${({ theme }) => theme.font.lineHeight.lg};
  font-weight: bold;
  margin-left: ${({ theme, isWritingAssistantHomeEnabled }) =>
    isWritingAssistantHomeEnabled ? spacing.px32 : theme.spacing.lg};

  @media only screen and (max-width: ${({ theme, isWritingAssistantHomeEnabled }) =>
      isWritingAssistantHomeEnabled
        ? breakpoints.md.max
        : themeBreakPoints.small(theme)}) {
    display: none;
  }

  :after {
    ${navAfterBar}
  }

  :hover:after {
    height: 5px;
    bottom: -1px;
  }

  ${({ isActive }) =>
    isActive &&
    css`
      :after {
        ${navAfterBar}
        height: 5px;
      }
    `}
`

export const UserActions = styled.div`
  margin-right: ${spacing.px12};
  display: flex;
  align-items: center;
`

export const HamburgerDropdown = styled(Popover)`
  [role='region'] {
    left: 5px;
  }
`

export const SignedinDropdown = styled(Popover)`
  [role='region'] {
    right: 2px;
    top: calc(100% + 0.5rem + 3px);
  }
`
